import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIndividualizationContext } from '../../../../contexts/individualizationContext'
import Heading from '../../../../components/Heading'
import Text from '../../../../components/Text'
import Button from '../../../../components/Button'
import cx from 'classnames'
import styles from '../DefineAreas/DefineAreas.module.css'
import Sidebar from '../DefineAreas/Sidebar'
import Input from '../../../../components/Input'
import { faRotate } from '@fortawesome/free-solid-svg-icons'
import ColorPicker from '../../../../components/CircleColorPicker'
import Icon from '../../../../components/Icon'
import {
    addCanvasToWrapper,
    changeBackground,
    resizeEverything,
} from '../../../../helpers/resizeEverything'

const SetMeasurements: React.FC = () => {
    const { t } = useTranslation(['individualization'])
    const {
        canvas,
        measurements,
        setMeasurements,
        hasMeasurement,
        setHasMeasurement,
        activeStep,
        templates,
        activeTemplateId,
        setActiveTemplateId,
        activeTemplate,
        addMeasurement,
    } = useIndividualizationContext()
    const dimensionsWrapper = useRef<HTMLDivElement>(null)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)
    const [showTemplatesSidebar, setShowTemplatesSidebar] =
        useState<boolean>(false)

    const changePage = (template_id: string | number) => {
        const result = templates.find((template) => template.id === template_id)
        if (result && template_id !== activeTemplateId) {
            setActiveTemplateId(template_id)
        }
    }

    const toggleTemplatesSidebar = () => {
        setShowTemplatesSidebar((prev) => !prev)
    }

    useEffect(() => {
        setActiveTemplateId((prev) => {
            if (prev) return prev
            if (templates.length > 0) return templates[0].id
            else return null
        })
    }, [templates, setActiveTemplateId])

    useEffect(() => {
        if (activeStep !== 2 || !activeTemplateId || !hasMeasurement) return

        if (
            !measurements.some(
                (measurement) =>
                    measurement.line.data.template_id === activeTemplateId
            )
        ) {
            const measurement = addMeasurement(activeTemplateId)
            setMeasurements((prev) => {
                return [...prev, measurement]
            })
        }

        canvas.remove(
            ...canvas
                .getObjects()
                .filter(
                    (obj) =>
                        obj.data?.template_id !== activeTemplateId ||
                        obj.data?.type !== 'Measurement'
                )
        )

        measurements.forEach((measurement) => {
            if (measurement.line.data.template_id === activeTemplateId) {
                const found = canvas
                    .getObjects()
                    .find((obj) => obj.data?.id === measurement.line.data.id)
                if (!found)
                    canvas.add(
                        measurement.line,
                        measurement.start,
                        measurement.end
                    )
            }
        })

        canvas.renderAll()
    }, [
        hasMeasurement,
        activeStep,
        activeTemplateId,
        measurements,
        canvas,
        addMeasurement,
        setMeasurements,
    ])

    useEffect(() => {
        if (!hasMeasurement || activeStep !== 2 || !dimensionsWrapper.current)
            return

        resizeObserver.current = new ResizeObserver(() => {
            clearTimeout(timer.current)
            timer.current = setTimeout(() => {
                if (dimensionsWrapper.current) {
                    resizeEverything(canvas, dimensionsWrapper.current)
                    addCanvasToWrapper(canvas, dimensionsWrapper.current)
                }
            }, 500)
        })
        resizeObserver.current.observe(dimensionsWrapper.current)

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect()
                resizeObserver.current = null
            }
            if (timer.current) {
                clearTimeout(timer.current)
                timer.current = undefined
            }
        }
    }, [canvas, hasMeasurement, activeStep])

    useEffect(() => {
        if (activeTemplate) {
            changeBackground(activeTemplate, canvas)
        }
    }, [activeTemplate, canvas])

    const activeMeasurement = useMemo(() => {
        return measurements.find(
            (r) => r.line.data.template_id === activeTemplateId
        )
    }, [measurements, activeTemplateId])

    const changeMeasurementColor = useCallback(
        (color: string) => {
            setMeasurements((prev) => {
                return prev.map((ruler) => {
                    if (ruler.line.data.template_id === activeTemplateId) {
                        return {
                            ...ruler,
                            start: ruler.start.set('fill', color),
                            end: ruler.end.set('fill', color),
                            line: ruler.line.set('fill', color),
                        }
                    }
                    return ruler
                })
            })
            canvas.renderAll()
        },
        [canvas, setMeasurements, activeTemplateId]
    )

    const rotateMeasurement = useCallback(() => {
        setMeasurements((prev) => {
            return prev.map((measurement) => {
                if (measurement.line.data.template_id === activeTemplateId) {
                    const newMeasurement = addMeasurement(
                        measurement.line.data.template_id,
                        measurement.line.data.id,
                        measurement.start.fill as string,
                        measurement.line.getCenterPoint(),
                        measurement.line.data.orientation === 'horizontal'
                            ? 'vertical'
                            : 'horizontal',
                        measurement.line.data.orientation === 'horizontal'
                            ? measurement.line.width
                            : measurement.line.height,
                        measurement.line.data.real_length
                    )

                    canvas.remove(
                        measurement.line,
                        measurement.start,
                        measurement.end
                    )
                    canvas.add(
                        newMeasurement.line,
                        newMeasurement.start,
                        newMeasurement.end
                    )
                    canvas.renderAll()
                    return newMeasurement
                }
                return measurement
            })
        })
        canvas.renderAll()
    }, [setMeasurements, canvas, activeTemplateId, addMeasurement])

    const setMeasurementData = useCallback(
        (property: string, value: number | string) => {
            setMeasurements((prev) => {
                return prev.map((measurement) => {
                    if (
                        measurement.line.data.template_id === activeTemplateId
                    ) {
                        return {
                            ...measurement,
                            line: measurement.line.set('data', {
                                ...measurement.line.data,
                                [property]: value,
                            }),
                        }
                    }
                    return measurement
                })
            })
            canvas.renderAll()
        },
        [setMeasurements, canvas, activeTemplateId]
    )

    return activeStep === 2 ? (
        <>
            <Heading level={3} classes={'zr-mt-4'}>
                {t('progress.set_dimensions')}
            </Heading>
            <Text classes={'zr-my-4'}>{t('set_dimensions.description')}</Text>
            <Button
                hierarchy={!hasMeasurement ? 'primary' : 'secondary'}
                text={
                    !hasMeasurement
                        ? t('set_dimensions.activate')
                        : t('set_dimensions.deactivate')
                }
                fn={() => setHasMeasurement(!hasMeasurement)}
            />
            {hasMeasurement && (
                <div className={cx(styles['page-wrapper'], 'zr-my-6')}>
                    <div
                        ref={dimensionsWrapper}
                        className={cx(styles['wrapper-container'])}
                    >
                        <Sidebar
                            templates={templates}
                            changeTemplate={changePage}
                            active={activeTemplateId}
                            show={showTemplatesSidebar}
                            toggleSidebar={toggleTemplatesSidebar}
                            colors={{ background: '#FC0', text: '#000' }}
                        />
                        <div className={cx(styles['measurement-wrapper'])}>
                            <div
                                onClick={() => rotateMeasurement()}
                                className={cx(styles['menu-icon'])}
                            >
                                <Icon
                                    icon={faRotate}
                                    size={'lg'}
                                    color={'#000'}
                                />
                            </div>
                        </div>
                        <div className={cx(styles['measurement-wrapper-text'])}>
                            {t('set_dimensions.rotate')}
                        </div>
                    </div>
                    {activeTemplate && (
                        <div className={cx(styles['right-wrapper'])}>
                            <Heading level={3}>{activeTemplate.name}</Heading>
                            <Input
                                classes={'zr-mt-4'}
                                type={'number'}
                                placeholder={'Maß in mm'}
                                property={'real_length'}
                                value={
                                    activeMeasurement?.line.data.real_length ??
                                    ''
                                }
                                onChange={(property, value) =>
                                    setMeasurementData(property, value)
                                }
                            />
                            <div
                                className={
                                    'zr-d-flex zr-row zr-align-center zr-my-4 zr-justify-start zr-w-100'
                                }
                            >
                                <ColorPicker
                                    classes={'zr-mr-4'}
                                    property={'color'}
                                    value={
                                        activeMeasurement?.start.fill as string
                                    }
                                    onChange={(_, val) =>
                                        changeMeasurementColor(val)
                                    }
                                    label={t('set_dimensions.line_color')}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    ) : null
}

export default SetMeasurements
